import styled from 'styled-components';
import React, {FC} from 'react';

import {mediaBreakpointDown, transition} from '../../../../lib/styleVariables';
import {TertiaryLink} from '../../../molecules/buttonsR4';
import {Icon} from '../../../molecules/icon';
import {StylableProps} from '../../../../lib/styleHelpers';
import {fontSizesMixinsMobileR4, fontSizesMixinsR4, paletteR4} from '../../../../lib/styleVariablesR4';
import {Image, ImageProps} from '../../../common/image';

export interface ImageTextCarouselItemProps<C = string> extends StylableProps {
  imgSrc: ImageProps['fluid'];
  buttonText?: string;
  buttonLink?: string;
  title: string;
  description?: string;
  content: C;
  isOpen: boolean;
  selectedItemColor?: string;
  hasImage?: boolean;
}

const StyledItemContainer = styled.div<{isOpen: boolean}>`
  cursor: pointer;
  width: 100%;
  transition: background ${transition.fast};
`;

const StyledBodyContainer = styled.div<{isOpen: boolean}>`
  padding: 24px 0;
  ${mediaBreakpointDown('tablet')} {
    padding: 18px 0 ${props => (props.isOpen ? 24 : 18)}px 0;
  }
`;

const StyledTitleWrapper = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
`;

const StyledTitle = styled.h4<{selectedItemColor?: string; isOpen: boolean}>`
  ${fontSizesMixinsR4.h4()};
  color: ${props =>
    (props.isOpen && props.selectedItemColor ? props.selectedItemColor : paletteR4.black.base)};
  flex-grow: 1;
  margin: 0;

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h4()};
  }
`;

const StyledTitleChevron = styled(Icon)<{selectedItemColor?: string; isOpen: boolean}>`
  color: ${props => (props.isOpen ? props.selectedItemColor : paletteR4.black.base)};
  height: 24px;
  margin-left: 5px;
  width: 24px;
`;

const StyledContent = styled.p`
  margin-bottom: 24px;
  ${fontSizesMixinsR4.p2()};
  color: ${paletteR4.black.base};
`;

const StyledContentWrapper = styled.div<{isOpen: boolean}>`
  display: ${props => (props.isOpen ? 'block' : 'none')};
  margin-top: 24px;
  ${mediaBreakpointDown('tablet')} {
    margin-top: 16px;
  }
`;

const StyledImg = styled(Image)`
  display: none;

  object-fit: cover;
  margin-bottom: 24px;
  height: 230px;
  width: 100%;
  border-radius: 10px;

  ${mediaBreakpointDown('tablet')} {
    display: block;
  }
`;

const StyledTertiaryLink = styled(TertiaryLink)`
  color: ${paletteR4.black.base};
  display: inline-block;
  ${fontSizesMixinsR4.h5()};
  &:hover {
    color: ${paletteR4.black.base};
  }
`;

export const ImageTextCarouselItem: FC<ImageTextCarouselItemProps> = props => (
  <StyledItemContainer isOpen={props.isOpen} className={props.className}>
    <StyledBodyContainer isOpen={props.isOpen}>
      <StyledTitleWrapper>
        <StyledTitle selectedItemColor={props.selectedItemColor} isOpen={props.isOpen}>
          {props.title}
        </StyledTitle>
        <StyledTitleChevron
          selectedItemColor={props.selectedItemColor}
          isOpen={props.isOpen}
          name={props.isOpen ? 'chevronUp' : 'chevronDown'}
        />
      </StyledTitleWrapper>
      <StyledContentWrapper isOpen={props.isOpen}>
        {props.hasImage && <StyledImg fluid={props.imgSrc} />}
        <StyledContent>{props.content}</StyledContent>
        {props.buttonLink && props.buttonText && (
          <StyledTertiaryLink href={props.buttonLink}>{props.buttonText}</StyledTertiaryLink>
        )}
      </StyledContentWrapper>
    </StyledBodyContainer>
  </StyledItemContainer>
);
